import React, { useContext } from 'react';
import { useAuthQuery as useQuery } from '../../../shared/hooks/useAuthQuery';
import { api } from '../../../convex/_generated/api';
import { Id } from '../../../convex/_generated/dataModel';
import { BaseContext } from '../../../shared/base-context';
import { TrophyIcon } from '@heroicons/react/24/outline';
import { FAQHelpIcon } from '../../../shared/components/FAQHelpIcon';
import { BadgeIcon } from '../../../shared/components/BadgeIcon';

export const XpProgressPanel: React.FC = () => {
  const { convexUserId } = useContext(BaseContext);

  const userXpData = useQuery(
    api.user_xp.getUserXpWithLevelInfo,
    convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip"
  );

  if (!userXpData) {
    return null;
  }

  const {
    total_xp,
    overall_level,
    badge_name,
    badge_icon,
    xp_to_next_level,
    progress_percent,
    is_max_level,
    last_xp_earned_amount,
    last_xp_earned_reason,
  } = userXpData;

  const getReasonLabel = (reason: string) => {
    switch (reason) {
      case 'session_complete':
        return 'Session completed';
      case 'skill_mastery':
        return 'Skill mastered';
      case 'streak_bonus':
        return 'Streak bonus';
      default:
        return reason;
    }
  };

  const nextLevelXp = total_xp + xp_to_next_level;
  const progressPercent = nextLevelXp > 0 ? Math.round((total_xp / nextLevelXp) * 100) : 0;

  return (
    <div className="w-full border-2 border-border/50 bg-card rounded-xl overflow-hidden">
      <div className="p-6">
        {/* Compact header with badge and level */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {badge_icon ? (
              <BadgeIcon icon={badge_icon} className="h-6 w-6 text-primary" />
            ) : (
              <TrophyIcon className="h-6 w-6 text-primary" />
            )}
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-foreground">{badge_name || `Level ${overall_level}`}</span>
                <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full">
                  Lv.{overall_level}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right">
              <span className="text-lg font-semibold text-foreground">{total_xp}</span>
              {!is_max_level && (
                <span className="text-sm text-muted-foreground"> / {nextLevelXp} pts</span>
              )}
            </div>
            <FAQHelpIcon anchor="progress" size="sm" tooltipText="How does progress work?" />
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-2 bg-muted rounded-full overflow-hidden mb-2">
          <div
            className="h-full bg-primary transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        {/* Points to next level */}
        <div className="text-xs text-muted-foreground">
          {is_max_level ? (
            'Max level reached!'
          ) : (
            `${xp_to_next_level} pts to Level ${overall_level + 1}`
          )}
        </div>

        {/* Recent XP Earned - compact inline */}
        {last_xp_earned_amount && last_xp_earned_amount > 0 && (
          <div className="mt-3 pt-3 border-t border-border/30 flex items-center gap-2 text-sm">
            <span className="text-green-600 font-medium">+{last_xp_earned_amount} pts</span>
            <span className="text-muted-foreground">
              {getReasonLabel(last_xp_earned_reason || '')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
